import { useState, useEffect } from 'react';
import { getEnvironmentBadge, ENV_LABELS, ENV_COLORS } from '../utils/environment';
import type { Environment } from '../utils/environment';

/**
 * Environment indicator overlay — shown in every NON-production environment
 * (DEV / UAT / staging), hidden in prod. Two layers:
 *   1. A small fixed corner pill, always visible in-app so a tester never
 *      mistakes DEV/UAT for PROD.
 *   2. A dynamically-badged favicon / apple-touch-icon (canvas) so the browser
 *      tab + home-screen bookmark icon carry the env label too — the closest a
 *      web app can get to "overlay on the app icon" at runtime (the installed
 *      PWA manifest icon is a static PNG and can't be stamped per-environment
 *      without committing per-env assets).
 *
 * Source of truth: GET /api/version `env` (authoritative — the backend knows its
 * deployment), with a hostname fallback so the badge still shows before/without
 * that call (e.g. offline). Prod => render nothing.
 */
export default function EnvBadge() {
  const [env, setEnv] = useState<Exclude<Environment, 'PROD'> | null>(() => getEnvironmentBadge());

  useEffect(() => {
    let cancelled = false;
    fetch('/api/version', { cache: 'no-store' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (cancelled) return;
        if (d?.env && d.env !== 'production' && d.env !== 'prod') {
          // Map common backend env names to a known environment. An env we
          // don't recognise is still non-prod, so fall back to the
          // hostname-derived value rather than hiding the badge entirely --
          // showing nothing on a non-prod deployment is the one outcome this
          // component exists to prevent.
          const e = String(d.env).toLowerCase();
          if (e.startsWith('dev')) setEnv('DEV');
          else if (e.startsWith('uat') || e.startsWith('stag')) setEnv('UAT');
        } else if (d?.env) {
          setEnv(null); // explicitly production
        }
      })
      .catch(() => {/* keep hostname-derived label */});
    return () => { cancelled = true; };
  }, []);

  // Stamp the favicon / apple-touch-icon with the SHORT env code -- the 192px
  // icon ribbon has no room for the full environment name.
  useEffect(() => {
    if (!env) return;
    badgeFavicon(env, ENV_COLORS[env]);
  }, [env]);

  if (!env) return null;

  const label = ENV_LABELS[env];

  return (
    <div
      aria-label={`${label} environment`}
      style={{
        position: 'fixed',
        // Bottom-left so it never overlaps the nav controls (logo top-left;
        // avatar + hamburger top-right). Clear of the home-indicator via inset.
        bottom: 'max(8px, env(safe-area-inset-bottom))',
        left: 'max(8px, env(safe-area-inset-left))',
        zIndex: 99999,
        background: ENV_COLORS[env], color: '#fff',
        fontSize: 10, fontWeight: 800, letterSpacing: '.1em',
        padding: '2px 7px', borderRadius: 4,
        boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
        pointerEvents: 'none', userSelect: 'none',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
  );
}

/**
 * Draw the env label as a ribbon over a copy of the app icon and swap it into
 * the favicon + apple-touch-icon links. Best-effort: if canvas/icon load fails,
 * the in-app pill still conveys the environment.
 */
function badgeFavicon(label: string, color: string) {
  try {
    const src = '/icons/icon-192.png';
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // 192px so the apple-touch-icon (home-screen bookmark) stays sharp; the
      // browser downscales it for the favicon tab.
      const size = 192;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, size, size);

      // Bottom ribbon band with the env label.
      const bandH = Math.round(size * 0.34);
      ctx.fillStyle = color;
      ctx.fillRect(0, size - bandH, size, bandH);
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.round(bandH * 0.62)}px -apple-system, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, size / 2, size - bandH / 2 + 1);

      const url = canvas.toDataURL('image/png');
      setIconHref('link[rel="icon"][type="image/png"]', url);
      setIconHref('link[rel="apple-touch-icon"]', url);
    };
    img.src = src;
  } catch {
    /* non-fatal — the in-app pill remains */
  }
}

function setIconHref(selector: string, href: string) {
  const el = document.querySelector<HTMLLinkElement>(selector);
  if (el) el.href = href;
}
